import { useState, useMemo, useEffect } from "react";
import BotonMapaProceso from "./BotonMapaProceso";
import { subirDatosSingle } from "./syncSupabase";
import { authHeader } from './supabase';

const STORAGE_CONFIG = 'alqos_cnc_config';

const CONFIG_DEFAULT = {
  costoHoraGeneral: '',
  costoHoraProduccion: '',
  costoHoraCalidad: '',
  costoHoraAdmin: '',
  costoKgCartulinaHoja: '',
  costoKgCartulinaRollo: '',
  costoKgPapel: '',
  costoKgTinta: '',
  costoKgOtroMaterial: '',
  cotizacionUSD: '',
  horasReprocesoPorNC: '2',
  personasPorReproceso: '2',
  horasParoTPM: '4',
  personasPorParo: '3',
};

const storage = { get: (k,def=[]) => { try { return JSON.parse(localStorage.getItem(k)||JSON.stringify(def)); } catch { return def; } } };

function calcularCNC(config) {
  const ncs         = storage.get('alqos_ncs');
  const tpm         = storage.get('alqos_tpm');
  const fsc         = storage.get('alqos_fsc_arqueo');
  const ots         = storage.get('alqos_ots');
  const caps        = storage.get('alqos_caps');
  const auditorias  = storage.get('alqos_auditorias');
  const ingresos    = storage.get('alqos_ingreso_materiales');
  const programa    = storage.get('alqos_programa_auditorias');
  const centrosCosto = storage.get('alqos_centros_costo', []);
  const maquinas     = centrosCosto.filter(r => r.tipoRegistro === 'maquina');
  const materiales   = centrosCosto.filter(r => r.tipoRegistro === 'material');

  const hGen   = Number(config.costoHoraGeneral||0);
  const hProd  = Number(config.costoHoraProduccion||0)||hGen;
  const hCal   = Number(config.costoHoraCalidad||0)||hGen;
  const hRep   = Number(config.horasReprocesoPorNC||2);
  const persRep = Number(config.personasPorReproceso||2);
  const hParo  = Number(config.horasParoTPM||4);
  const persParo = Number(config.personasPorParo||3);
  const usd    = Number(config.cotizacionUSD||1);

  // Costo promedio materiales dinámicos
  const matConCosto = materiales.filter(m=>Number(m.costoKg)>0);
  const kgOtro = matConCosto.length > 0
    ? matConCosto.reduce((a,m)=>a+Number(m.costoKg),0) / matConCosto.length
    : 0;

  // Costo promedio máquinas dinámicas
  const maqConCosto = maquinas.filter(m=>Number(m.costoHora)>0);
  const hMaq = maqConCosto.length > 0
    ? maqConCosto.reduce((a,m)=>a+Number(m.costoHora),0) / maqConCosto.length
    : 0;

  const mesActual = new Date().getMonth();
  const anioActual = new Date().getFullYear();
  const esMes = (fecha) => { try { const f=new Date(fecha); return f.getMonth()===mesActual&&f.getFullYear()===anioActual; } catch { return false; } };

  // ── FALLAS INTERNAS ──────────────────────────────────────
  // NCs abiertas → horas de gestión + reproceso
  const ncsAbiertas = ncs.filter(n=>n.estado!=='cerrada'&&n.estado!=='Cerrada');
  const ncsMes = ncs.filter(n=>esMes(n.fechaHora||n.fechaCreacion));
  const costoNCs = ncsMes.length * hRep * persRep * hProd;

  // Scrap FSC → kg descartados
  const scrapMes = fsc.filter(m=>esMes(m.fecha)).reduce((a,m)=>a+Number(m.kgScrap||0),0);
  const costoScrap = scrapMes * kgOtro;

  // OTs rechazadas/reprocesadas
  const otsRechazadas = ots.filter(o=>o.estado==='rechazado'&&esMes(o.fecha||o.fechaCreacion));
  const costoOTsRechazadas = otsRechazadas.length * hRep * persRep * hProd;

  // Materiales en cuarentena
  const matCuarentena = ingresos.filter(i=>i.estado==='cuarentena');
  const costoMatCuarentena = matCuarentena.length * kgOtro * 10; // estimado 10kg promedio

  // Paros TPM correctivos
  const parosCorrectivos = tpm.filter(t=>t.tipo==='correctivo'&&esMes(t.fecha));
  const costoParos = parosCorrectivos.length * hParo * persParo * (hMaq||hProd);

  const fallaInternas = costoNCs + costoScrap + costoOTsRechazadas + costoMatCuarentena + costoParos;

  // ── FALLAS EXTERNAS ──────────────────────────────────────
  // Devoluciones (ingresos devueltos)
  const devoluciones = ingresos.filter(i=>i.estado==='devuelto'&&esMes(i.fecha||i.fechaCreacion));
  const costoDevoluciones = devoluciones.length * hRep * persRep * hProd * 3; // x3 por impacto cliente

  // NCs de clientes (origen externo)
  const ncsCliente = ncsMes.filter(n=>n.origen==='cliente'||n.tipo==='cliente');
  const costoNcsCliente = ncsCliente.length * hRep * persRep * hProd * 2;

  const fallasExternas = costoDevoluciones + costoNcsCliente;

  // ── PREVENCIÓN ────────────────────────────────────────────
  // Capacitaciones
  const capsMes = caps.filter(c=>esMes(c.fecha||c.fechaCreacion));
  const costoCaps = capsMes.reduce((a,c)=>a+(Number(c.duracion||2)*Number(c.participantes||1)*hGen),0);

  // TPM preventivo
  const tpmPrev = tpm.filter(t=>t.tipo==='preventivo'&&esMes(t.fecha));
  const costoTPMPrev = tpmPrev.length * (hParo/2) * hProd;

  // Auditorías planificadas
  const audPlanif = programa.filter(p=>esMes(p.fecha||p.fechaCreacion));
  const costoAudPlanif = audPlanif.length * 4 * hCal; // 4hs por auditoría

  const prevencion = costoCaps + costoTPMPrev + costoAudPlanif;

  // ── EVALUACIÓN ───────────────────────────────────────────
  // Auditorías ejecutadas
  const audEjec = auditorias.filter(a=>a.estado==='completada'&&esMes(a.fechaCreacion||a.fecha));
  const costoAudEjec = audEjec.length * 8 * hCal; // 8hs por auditoría

  // Inspecciones ingreso materiales
  const inspecciones = ingresos.filter(i=>esMes(i.fecha||i.fechaCreacion));
  const costoInsp = inspecciones.length * 0.5 * hCal; // 30min por inspección

  const evaluacion = costoAudEjec + costoInsp;

  const total = fallaInternas + fallasExternas + prevencion + evaluacion;
  const totalUSD = usd > 0 ? total / usd : 0;

  return {
    fallaInternas, fallasExternas, prevencion, evaluacion, total, totalUSD,
    detalle: {
      costoNCs, costoScrap, costoOTsRechazadas, costoMatCuarentena, costoParos,
      costoDevoluciones, costoNcsCliente,
      costoCaps, costoTPMPrev, costoAudPlanif,
      costoAudEjec, costoInsp,
    },
    unidades: {
      ncsMes: ncsMes.length, ncsAbiertas: ncsAbiertas.length,
      scrapMes: Math.round(scrapMes*100)/100,
      otsRechazadas: otsRechazadas.length,
      matCuarentena: matCuarentena.length,
      parosCorrectivos: parosCorrectivos.length,
      devoluciones: devoluciones.length,
      ncsCliente: ncsCliente.length,
      capsMes: capsMes.length,
      tpmPrev: tpmPrev.length,
      audPlanif: audPlanif.length,
      audEjec: audEjec.length,
      inspecciones: inspecciones.length,
    },
    configurado: hGen > 0 || hProd > 0 || kgOtro > 0 || hMaq > 0,
  };
}

function fmt(n) { return Math.round(n).toLocaleString('es-AR'); }

export default function CostosNoCalidad({ onVolver, oscuro, usuario, onNavegar }) {
  const [config, setConfig] = useState(() => storage.get(STORAGE_CONFIG, CONFIG_DEFAULT));
  const [tab, setTab] = useState('dashboard');

  const setC = (k,v) => setConfig(c=>({...c,[k]:v}));

  const guardarConfig = () => {
    localStorage.setItem(STORAGE_CONFIG, JSON.stringify(config));
    subirDatosSingle(STORAGE_CONFIG, 'cnc_config');
    alert('✅ Configuración guardada. Los costos se recalcularán automáticamente.');
    setTab('dashboard');
  };

  const cnc = useMemo(() => calcularCNC(config), [config]);

  const inp = {width:'100%',padding:'8px 10px',borderRadius:'8px',border:'1px solid #E5E7EB',fontSize:'13px',boxSizing:'border-box',fontFamily:'Inter, sans-serif'};
  const lbl = {fontSize:'11px',fontWeight:'600',color:'#6B7280',marginBottom:'4px',display:'block'};
  const secTit = {fontSize:'11px',fontWeight:'700',color:'#1C3FAA',margin:'16px 0 10px',paddingBottom:'5px',borderBottom:'1px solid #E8E9F6',textTransform:'uppercase',letterSpacing:'0.08em'};

  const CATEGORIAS = [
    { key:'fallaInternas',  label:'Fallas internas',  color:'#dc2626', bg:'#fee2e2', icon:'🔴', desc:'Reprocesos, scrap, paros, cuarentena' },
    { key:'fallasExternas', label:'Fallas externas',  color:'#d97706', bg:'#fef3c7', icon:'🟠', desc:'Devoluciones, reclamos de clientes' },
    { key:'prevencion',     label:'Prevención',       color:'#16a34a', bg:'#dcfce7', icon:'🟢', desc:'Capacitaciones, TPM prev., auditorías planif.' },
    { key:'evaluacion',     label:'Evaluación',       color:'#2A7FFF', bg:'#dbeafe', icon:'🔵', desc:'Auditorías ejecutadas, inspecciones' },
  ];

  const pct = (v) => cnc.total > 0 ? Math.round((v/cnc.total)*100) : 0;

  return (
    <div style={{padding:'24px',fontFamily:'Inter, sans-serif',background:'#E8E9F6',minHeight:'100vh'}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <button onClick={onVolver} style={{background:'none',border:'1px solid #E5E7EB',borderRadius:'8px',padding:'6px 12px',fontSize:'12px',cursor:'pointer',color:'#6B7280'}}>← Menú</button>
            <span style={{fontSize:'22px'}}>💸</span>
            <h1 style={{fontSize:'20px',fontWeight:'700',color:'#1C3FAA',margin:0}}>Costos de No Calidad</h1>
            <span style={{fontSize:'11px',background:'#dbeafe',color:'#1C3FAA',padding:'2px 8px',borderRadius:'6px',fontWeight:'600'}}>ISO 9001 §10.1</span>
          </div>
          <p style={{fontSize:'12px',color:'#6B7280',marginTop:'4px',marginLeft:'32px'}}>
            Cálculo automático desde NC · TPM · FSC · OTs · Capacitaciones · Auditorías
          </p>
        </div>
        <div style={{display:'flex',gap:'8px'}}>
          {!cnc.configurado && (
            <span style={{background:'#fef3c7',color:'#d97706',fontSize:'11px',padding:'6px 12px',borderRadius:'8px',fontWeight:'600'}}>
              ⚠️ Configurá los costos para ver montos
            </span>
          )}
          <button onClick={()=>setTab('config')}
            style={{background:tab==='config'?'#1C3FAA':'white',color:tab==='config'?'white':'#1C3FAA',border:'1px solid #1C3FAA',borderRadius:'10px',padding:'8px 14px',fontSize:'12px',fontWeight:'600',cursor:'pointer'}}>
            ⚙️ Configurar costos
          </button>
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="costos_no_calidad" oscuro={oscuro} />}
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:'4px',background:'white',padding:'4px',borderRadius:'10px',border:'1px solid #E5E7EB',width:'fit-content',marginBottom:'20px'}}>
        {[['dashboard','📊 Dashboard'],['detalle','🔍 Detalle'],['tendencia','📈 Tendencia'],['historial','📚 Historial'],['aria','🤖 ARIA'],['config','⚙️ Configuración']].map(([id,lb])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{padding:'7px 16px',borderRadius:'8px',border:'none',fontSize:'12px',fontWeight:'500',cursor:'pointer',background:tab===id?'#1C3FAA':'transparent',color:tab===id?'white':'#6B7280'}}>{lb}</button>
        ))}
      </div>

      {/* ── DASHBOARD ── */}
      {tab==='dashboard' && (<>

        {/* Total */}
        <div style={{background:'#1C3FAA',borderRadius:'16px',padding:'24px',marginBottom:'16px',color:'white'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'20px',alignItems:'center'}}>
            <div>
              <div style={{fontSize:'12px',opacity:0.7,marginBottom:'4px'}}>CNC Total del mes</div>
              <div style={{fontSize:'36px',fontWeight:'800'}}>
                {cnc.configurado ? `$${fmt(cnc.total)}` : '— Sin configurar'}
              </div>
              {cnc.configurado && cnc.totalUSD>0 && (
                <div style={{fontSize:'14px',opacity:0.8,marginTop:'4px'}}>USD {fmt(cnc.totalUSD)}</div>
              )}
            </div>
            <div>
              <div style={{fontSize:'12px',opacity:0.7,marginBottom:'8px'}}>Distribución por categoría</div>
              {CATEGORIAS.map(cat=>(
                <div key={cat.key} style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'5px'}}>
                  <div style={{width:`${pct(cnc[cat.key])}%`,maxWidth:'100%',height:'6px',background:cat.color,borderRadius:'3px',minWidth:'4px'}}></div>
                  <span style={{fontSize:'11px',opacity:0.8,whiteSpace:'nowrap'}}>{pct(cnc[cat.key])}% {cat.label}</span>
                </div>
              ))}
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:'12px',opacity:0.7,marginBottom:'4px'}}>Mayor impacto</div>
              <div style={{fontSize:'16px',fontWeight:'700'}}>
                {CATEGORIAS.sort((a,b)=>cnc[b.key]-cnc[a.key])[0]?.icon} {CATEGORIAS.sort((a,b)=>cnc[b.key]-cnc[a.key])[0]?.label}
              </div>
              <div style={{fontSize:'12px',opacity:0.7,marginTop:'8px'}}>
                {cnc.unidades.ncsMes} NCs · {cnc.unidades.scrapMes}kg scrap · {cnc.unidades.parosCorrectivos} paros
              </div>
            </div>
          </div>
        </div>

        {/* Cards por categoría */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'12px',marginBottom:'16px'}}>
          {CATEGORIAS.map(cat=>(
            <div key={cat.key} style={{background:'white',borderRadius:'14px',padding:'18px',border:`1.5px solid ${cat.color}30`}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'12px'}}>
                <div>
                  <div style={{fontSize:'20px',marginBottom:'4px'}}>{cat.icon}</div>
                  <div style={{fontSize:'14px',fontWeight:'700',color:'#111827'}}>{cat.label}</div>
                  <div style={{fontSize:'11px',color:'#6B7280',marginTop:'2px'}}>{cat.desc}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:'22px',fontWeight:'800',color:cat.color}}>
                    {cnc.configurado ? `$${fmt(cnc[cat.key])}` : '—'}
                  </div>
                  <div style={{fontSize:'11px',color:'#6B7280'}}>{pct(cnc[cat.key])}% del total</div>
                </div>
              </div>
              {/* Barra */}
              <div style={{background:'#F3F4F6',borderRadius:'4px',height:'6px',marginBottom:'10px'}}>
                <div style={{width:`${pct(cnc[cat.key])}%`,height:'6px',background:cat.color,borderRadius:'4px',transition:'width 0.5s'}}></div>
              </div>
              {/* Unidades */}
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                {cat.key==='fallaInternas' && (<>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.ncsMes} NCs mes</span>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.scrapMes}kg scrap</span>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.parosCorrectivos} paros</span>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.otsRechazadas} OTs rechazo</span>
                </>)}
                {cat.key==='fallasExternas' && (<>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.devoluciones} devoluciones</span>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.ncsCliente} NCs cliente</span>
                </>)}
                {cat.key==='prevencion' && (<>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.capsMes} capacitaciones</span>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.tpmPrev} TPM prev.</span>
                </>)}
                {cat.key==='evaluacion' && (<>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.audEjec} auditorías</span>
                  <span style={{fontSize:'10px',background:'#F3F4F6',padding:'2px 7px',borderRadius:'5px',color:'#374151'}}>{cnc.unidades.inspecciones} inspecciones</span>
                </>)}
              </div>
            </div>
          ))}
        </div>

        {/* Alerta si no configurado */}
        {!cnc.configurado && (
          <div style={{background:'#fef3c7',borderRadius:'12px',padding:'16px 20px',border:'1px solid #fde68a',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontSize:'13px',fontWeight:'700',color:'#d97706',marginBottom:'2px'}}>⚙️ Configuración de costos pendiente</div>
              <div style={{fontSize:'12px',color:'#6B7280'}}>Los datos en unidades ya están disponibles. Cargá los valores monetarios para ver el CNC en pesos.</div>
            </div>
            <button onClick={()=>setTab('config')}
              style={{background:'#d97706',color:'white',border:'none',borderRadius:'8px',padding:'8px 16px',fontSize:'12px',fontWeight:'600',cursor:'pointer',whiteSpace:'nowrap'}}>
              Configurar ahora
            </button>
          </div>
        )}
      </>)}

      {/* ── DETALLE ── */}
      {tab==='detalle' && (
        <div style={{background:'white',borderRadius:'14px',border:'1px solid #E5E7EB',overflow:'hidden'}}>
          {CATEGORIAS.map(cat=>(
            <div key={cat.key} style={{padding:'16px 20px',borderBottom:'1px solid #F3F4F6'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                <div style={{fontSize:'13px',fontWeight:'700',color:'#111827'}}>{cat.icon} {cat.label}</div>
                <div style={{fontSize:'14px',fontWeight:'800',color:cat.color}}>{cnc.configurado?`$${fmt(cnc[cat.key])}`:'Sin configurar'}</div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                {cat.key==='fallaInternas' && [
                  ['NCs del mes',cnc.unidades.ncsMes,'unidades',cnc.detalle.costoNCs],
                  ['Scrap FSC',cnc.unidades.scrapMes,'kg',cnc.detalle.costoScrap],
                  ['OTs rechazadas',cnc.unidades.otsRechazadas,'unidades',cnc.detalle.costoOTsRechazadas],
                  ['Mat. cuarentena',cnc.unidades.matCuarentena,'lotes',cnc.detalle.costoMatCuarentena],
                  ['Paros correctivos',cnc.unidades.parosCorrectivos,'eventos',cnc.detalle.costoParos],
                ].map(([label,uni,unit,costo])=>(
                  <div key={label} style={{background:'#F9FAFB',borderRadius:'8px',padding:'10px',border:'1px solid #E5E7EB'}}>
                    <div style={{fontSize:'11px',color:'#6B7280',marginBottom:'3px'}}>{label}</div>
                    <div style={{fontSize:'16px',fontWeight:'700',color:'#374151'}}>{uni} <span style={{fontSize:'10px',color:'#9CA3AF'}}>{unit}</span></div>
                    <div style={{fontSize:'11px',color:cat.color,fontWeight:'600',marginTop:'2px'}}>{cnc.configurado?`$${fmt(costo)}`:'—'}</div>
                  </div>
                ))}
                {cat.key==='fallasExternas' && [
                  ['Devoluciones',cnc.unidades.devoluciones,'unidades',cnc.detalle.costoDevoluciones],
                  ['NCs de clientes',cnc.unidades.ncsCliente,'unidades',cnc.detalle.costoNcsCliente],
                ].map(([label,uni,unit,costo])=>(
                  <div key={label} style={{background:'#F9FAFB',borderRadius:'8px',padding:'10px',border:'1px solid #E5E7EB'}}>
                    <div style={{fontSize:'11px',color:'#6B7280',marginBottom:'3px'}}>{label}</div>
                    <div style={{fontSize:'16px',fontWeight:'700',color:'#374151'}}>{uni} <span style={{fontSize:'10px',color:'#9CA3AF'}}>{unit}</span></div>
                    <div style={{fontSize:'11px',color:cat.color,fontWeight:'600',marginTop:'2px'}}>{cnc.configurado?`$${fmt(costo)}`:'—'}</div>
                  </div>
                ))}
                {cat.key==='prevencion' && [
                  ['Capacitaciones',cnc.unidades.capsMes,'eventos',cnc.detalle.costoCaps],
                  ['TPM preventivo',cnc.unidades.tpmPrev,'órdenes',cnc.detalle.costoTPMPrev],
                  ['Auditorías planif.',cnc.unidades.audPlanif,'eventos',cnc.detalle.costoAudPlanif],
                ].map(([label,uni,unit,costo])=>(
                  <div key={label} style={{background:'#F9FAFB',borderRadius:'8px',padding:'10px',border:'1px solid #E5E7EB'}}>
                    <div style={{fontSize:'11px',color:'#6B7280',marginBottom:'3px'}}>{label}</div>
                    <div style={{fontSize:'16px',fontWeight:'700',color:'#374151'}}>{uni} <span style={{fontSize:'10px',color:'#9CA3AF'}}>{unit}</span></div>
                    <div style={{fontSize:'11px',color:cat.color,fontWeight:'600',marginTop:'2px'}}>{cnc.configurado?`$${fmt(costo)}`:'—'}</div>
                  </div>
                ))}
                {cat.key==='evaluacion' && [
                  ['Auditorías ejec.',cnc.unidades.audEjec,'eventos',cnc.detalle.costoAudEjec],
                  ['Inspecciones mat.',cnc.unidades.inspecciones,'ingresos',cnc.detalle.costoInsp],
                ].map(([label,uni,unit,costo])=>(
                  <div key={label} style={{background:'#F9FAFB',borderRadius:'8px',padding:'10px',border:'1px solid #E5E7EB'}}>
                    <div style={{fontSize:'11px',color:'#6B7280',marginBottom:'3px'}}>{label}</div>
                    <div style={{fontSize:'16px',fontWeight:'700',color:'#374151'}}>{uni} <span style={{fontSize:'10px',color:'#9CA3AF'}}>{unit}</span></div>
                    <div style={{fontSize:'11px',color:cat.color,fontWeight:'600',marginTop:'2px'}}>{cnc.configurado?`$${fmt(costo)}`:'—'}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── TENDENCIA ── */}
      {tab==='tendencia' && (
        <div style={{background:'white',borderRadius:'14px',padding:'24px',border:'1px solid #E5E7EB',textAlign:'center'}}>
          <div style={{fontSize:'40px',marginBottom:'12px'}}>📈</div>
          <div style={{fontSize:'14px',fontWeight:'600',color:'#374151',marginBottom:'8px'}}>Tendencia histórica</div>
          <div style={{fontSize:'12px',color:'#6B7280',marginBottom:'20px'}}>Disponible a partir del segundo mes de uso. ALQOS™ irá acumulando datos para comparar períodos.</div>
          <div style={{background:'#F9FAFB',borderRadius:'10px',padding:'16px',border:'1px solid #E5E7EB',display:'inline-block'}}>
            <div style={{fontSize:'12px',color:'#6B7280'}}>Mes actual</div>
            <div style={{fontSize:'24px',fontWeight:'800',color:'#1C3FAA',marginTop:'4px'}}>{cnc.configurado?`$${fmt(cnc.total)}`:'Sin configurar'}</div>
          </div>
        </div>
      )}

      {/* ── HISTORIAL ── */}
      {tab==='historial' && (
        <HistorialInformes />
      )}

      {/* ── ARIA ── */}
      {tab==='aria' && (
        <ARIACNCAnalisis cnc={cnc} config={config} />
      )}

      {/* ── CONFIGURACIÓN ── */}
      {tab==='config' && (
        <div style={{background:'white',borderRadius:'14px',padding:'24px',border:'1px solid #E5E7EB'}}>
          <div style={{fontSize:'13px',color:'#6B7280',marginBottom:'20px',background:'#F0F9FF',padding:'12px',borderRadius:'8px',border:'1px solid #BAE6FD'}}>
            💡 Cargá los valores que tengas disponibles. Los campos vacíos usarán el costo hora general como referencia. Podés actualizar estos valores en cualquier momento.
          </div>

          <div style={secTit}>💰 Costo de mano de obra (por hora)</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'12px',marginBottom:'8px'}}>
            <div><label style={lbl}>Costo hora general (referencia)</label><input style={inp} type="number" placeholder="Ej: 5000" value={config.costoHoraGeneral} onChange={e=>setC('costoHoraGeneral',e.target.value)}/></div>
            <div><label style={lbl}>Costo hora producción</label><input style={inp} type="number" placeholder="Ej: 5500" value={config.costoHoraProduccion} onChange={e=>setC('costoHoraProduccion',e.target.value)}/></div>
            <div><label style={lbl}>Costo hora calidad</label><input style={inp} type="number" placeholder="Ej: 6000" value={config.costoHoraCalidad} onChange={e=>setC('costoHoraCalidad',e.target.value)}/></div>
            <div><label style={lbl}>Costo hora administración</label><input style={inp} type="number" placeholder="Ej: 4500" value={config.costoHoraAdmin} onChange={e=>setC('costoHoraAdmin',e.target.value)}/></div>
          </div>

          <div style={secTit}>⚙️ Máquinas y materiales</div>
          <div style={{background:'#F0F9FF',borderRadius:'10px',padding:'16px',border:'1px solid #BAE6FD',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontSize:'13px',fontWeight:'700',color:'#1C3FAA',marginBottom:'4px'}}>Esto ahora se administra en Centros de Costo</div>
              <div style={{fontSize:'12px',color:'#6B7280'}}>Máquinas, materiales, velocidades y rendimientos viven en un único módulo compartido.</div>
            </div>
            <button onClick={()=>onNavegar && onNavegar('centrosCosto')}
              style={{background:'#1C3FAA',color:'white',border:'none',borderRadius:'8px',padding:'10px 16px',fontSize:'12px',fontWeight:'600',cursor:'pointer',whiteSpace:'nowrap'}}>
              ⚙️ Ir a Centros de Costo
            </button>
          </div>

          <div style={secTit}>🌍 Moneda y parámetros de estimación</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'12px',marginBottom:'8px'}}>
            <div><label style={lbl}>Cotización USD ($/dólar)</label><input style={inp} type="number" placeholder="Ej: 1050" value={config.cotizacionUSD} onChange={e=>setC('cotizacionUSD',e.target.value)}/></div>
            <div></div>
            <div><label style={lbl}>Horas estimadas por reproceso (NC)</label><input style={inp} type="number" placeholder="Ej: 2" value={config.horasReprocesoPorNC} onChange={e=>setC('horasReprocesoPorNC',e.target.value)}/></div>
            <div><label style={lbl}>Personas promedio por reproceso</label><input style={inp} type="number" placeholder="Ej: 2" value={config.personasPorReproceso} onChange={e=>setC('personasPorReproceso',e.target.value)}/></div>
            <div><label style={lbl}>Horas estimadas por paro TPM</label><input style={inp} type="number" placeholder="Ej: 4" value={config.horasParoTPM} onChange={e=>setC('horasParoTPM',e.target.value)}/></div>
            <div><label style={lbl}>Personas promedio por paro</label><input style={inp} type="number" placeholder="Ej: 3" value={config.personasPorParo} onChange={e=>setC('personasPorParo',e.target.value)}/></div>
          </div>

          <div style={{display:'flex',gap:'10px',marginTop:'24px'}}>
            <button onClick={()=>setTab('dashboard')} style={{flex:1,padding:'10px',borderRadius:'10px',border:'1px solid #E5E7EB',background:'white',fontSize:'13px',fontWeight:'600',cursor:'pointer',color:'#6B7280'}}>Cancelar</button>
            <button onClick={guardarConfig} style={{flex:2,padding:'10px',borderRadius:'10px',border:'none',background:'#1C3FAA',color:'white',fontSize:'13px',fontWeight:'600',cursor:'pointer'}}>💾 Guardar configuración</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ARIACNCAnalisis({ cnc, config }) {
  const [analisis, setAnalisis] = useState('');
  const [cargando, setCargando] = useState(false);

  const generar = async () => {
    setCargando(true); setAnalisis('');
    const prompt = `Sos consultor experto ISO 9001 para una pyme gráfica argentina. Generá un informe ejecutivo para Dirección con estos datos de Costos de No Calidad del mes:\n\nCNC TOTAL: $${fmt(cnc.total)} ARS | USD ${fmt(cnc.totalUSD)}\n- Fallas internas: $${fmt(cnc.fallaInternas)} | NCs: ${cnc.unidades.ncsMes} | Scrap: ${cnc.unidades.scrapMes}kg | Paros: ${cnc.unidades.parosCorrectivos} | OTs rechazadas: ${cnc.unidades.otsRechazadas} | Mat. cuarentena: ${cnc.unidades.matCuarentena}\n- Fallas externas: $${fmt(cnc.fallasExternas)} | Devoluciones: ${cnc.unidades.devoluciones} | NCs cliente: ${cnc.unidades.ncsCliente}\n- Prevención: $${fmt(cnc.prevencion)} | Capacitaciones: ${cnc.unidades.capsMes} | TPM prev: ${cnc.unidades.tpmPrev}\n- Evaluación: $${fmt(cnc.evaluacion)} | Auditorías: ${cnc.unidades.audEjec} | Inspecciones: ${cnc.unidades.inspecciones}\n\nEstructurá el informe con:\n## Resumen ejecutivo\n## Principales focos de pérdida\n## Relación prevención vs fallas\n## 3 acciones concretas para Dirección (con impacto estimado en ARS)\n## Semáforo: 🔴 Crítica / 🟡 Atención / 🟢 Controlada`;
    try {
      const res = await fetch('/api/aria', {
        method: 'POST',
        headers: { ...(await authHeader()), 'Content-Type': 'application/json', 'anthropic-version': '2023-06-01', },
        body: JSON.stringify({ model: 'claude-sonnet-4-5', max_tokens: 1500, messages: [{ role: 'user', content: prompt }] })
      });
      const data = await res.json();
      const texto = data.content?.[0]?.text || 'Sin respuesta';
      setAnalisis(texto);
      const mes = new Date().toISOString().slice(0,7);
      await fetch('/api/cnc-informes', {
        method: 'POST',
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ mes, datos_cnc: cnc, informe_texto: texto, usuario: 'admin' })
      }).then(r => { if(!r.ok) r.text().then(e => console.error('Error guardando informe:', e)); });
    } catch(e) { setAnalisis('Error de conexión.'); }
    setCargando(false);
  };

  const copiar = () => navigator.clipboard.writeText(analisis);

  const exportarCSV = () => {
    const filas = [
      ['Categoría','Subcategoría','Cantidad','Unidad','Costo ARS'],
      ['Fallas internas','NCs del mes',cnc.unidades.ncsMes,'unidades',cnc.detalle.costoNCs],
      ['Fallas internas','Scrap FSC',cnc.unidades.scrapMes,'kg',cnc.detalle.costoScrap],
      ['Fallas internas','OTs rechazadas',cnc.unidades.otsRechazadas,'unidades',cnc.detalle.costoOTsRechazadas],
      ['Fallas internas','Mat. cuarentena',cnc.unidades.matCuarentena,'lotes',cnc.detalle.costoMatCuarentena],
      ['Fallas internas','Paros correctivos',cnc.unidades.parosCorrectivos,'eventos',cnc.detalle.costoParos],
      ['Fallas externas','Devoluciones',cnc.unidades.devoluciones,'unidades',cnc.detalle.costoDevoluciones],
      ['Fallas externas','NCs de clientes',cnc.unidades.ncsCliente,'unidades',cnc.detalle.costoNcsCliente],
      ['Prevención','Capacitaciones',cnc.unidades.capsMes,'eventos',cnc.detalle.costoCaps],
      ['Prevención','TPM preventivo',cnc.unidades.tpmPrev,'órdenes',cnc.detalle.costoTPMPrev],
      ['Prevención','Auditorías planif.',cnc.unidades.audPlanif,'eventos',cnc.detalle.costoAudPlanif],
      ['Evaluación','Auditorías ejecutadas',cnc.unidades.audEjec,'eventos',cnc.detalle.costoAudEjec],
      ['Evaluación','Inspecciones',cnc.unidades.inspecciones,'ingresos',cnc.detalle.costoInsp],
      ['TOTAL','Fallas internas','','',cnc.fallaInternas],
      ['TOTAL','Fallas externas','','',cnc.fallasExternas],
      ['TOTAL','Prevención','','',cnc.prevencion],
      ['TOTAL','Evaluación','','',cnc.evaluacion],
      ['TOTAL GENERAL','','','',cnc.total],
      ['TOTAL USD','','','',cnc.totalUSD],
    ];
    const csv = filas.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CNC_${new Date().toISOString().slice(0,7)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{background:'white',borderRadius:'14px',padding:'24px',border:'1px solid #E5E7EB'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
        <div>
          <div style={{fontSize:'15px',fontWeight:'700',color:'#1C3FAA'}}>🤖 Análisis ARIA — Informe para Dirección</div>
          <div style={{fontSize:'12px',color:'#6B7280',marginTop:'2px'}}>Basado en los datos CNC del mes actual</div>
        </div>
        <div style={{display:'flex',gap:'8px'}}>
          <button onClick={exportarCSV} style={{padding:'8px 14px',borderRadius:'8px',border:'1px solid #E5E7EB',background:'white',fontSize:'12px',cursor:'pointer',color:'#6B7280'}}>📥 Exportar CSV</button>
          {analisis && <button onClick={copiar} style={{padding:'8px 14px',borderRadius:'8px',border:'1px solid #E5E7EB',background:'white',fontSize:'12px',cursor:'pointer',color:'#6B7280'}}>📋 Copiar</button>}
          <button onClick={generar} disabled={cargando} style={{padding:'8px 18px',borderRadius:'8px',border:'none',background:cargando?'#9B7BFF':'#7B5BF2',color:'white',fontSize:'13px',fontWeight:'600',cursor:cargando?'default':'pointer'}}>
            {cargando ? '⏳ Analizando...' : analisis ? '🔄 Regenerar' : '✨ Generar análisis'}
          </button>
        </div>
      </div>
      {!cnc.configurado && (
        <div style={{background:'#fef3c7',borderRadius:'10px',padding:'12px 16px',fontSize:'12px',color:'#d97706',marginBottom:'16px'}}>
          ⚠️ Configurá los costos primero para que el análisis incluya valores monetarios reales.
        </div>
      )}
      {!analisis && !cargando && (
        <div style={{textAlign:'center',padding:'40px',color:'#9CA3AF'}}>
          <div style={{fontSize:'40px',marginBottom:'12px'}}>🤖</div>
          <div style={{fontSize:'13px'}}>Presioná "Generar análisis" para que ARIA interprete los datos del mes y genere un informe ejecutivo listo para presentar a Dirección.</div>
        </div>
      )}
      {cargando && (
        <div style={{textAlign:'center',padding:'40px',color:'#7B5BF2'}}>
          <div style={{fontSize:'32px',marginBottom:'12px'}}>⏳</div>
          <div style={{fontSize:'13px',fontWeight:'600'}}>ARIA está analizando los datos...</div>
        </div>
      )}
      {analisis && !cargando && (
        <div style={{background:'#F8F7FF',borderRadius:'12px',padding:'20px',border:'1px solid #E0D9FF',fontSize:'13px',lineHeight:'1.8',color:'#2F3E2E',whiteSpace:'pre-wrap'}}>
          {analisis}
        </div>
      )}
    </div>
  );
}

function HistorialInformes() {
  const [informes, setInformes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [seleccionado, setSeleccionado] = useState(null);
  // useEffect ya importado en el componente principal

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este informe?')) return;
    await fetch('/api/cnc-informes?id=' + id, { method: 'DELETE', headers: await authHeader() });
    setInformes(prev => prev.filter(i => i.id !== id));
    if (seleccionado?.id === id) setSeleccionado(null);
  };

  useEffect(() => {
    (async () => {
      fetch('/api/cnc-informes', { headers: await authHeader() })
      .then(r => r.json())
      .then(data => { setInformes(data); setCargando(false); })
      .catch(() => setCargando(false));
    })();
  }, []);

  if (cargando) return (
    <div style={{textAlign:'center',padding:'40px',color:'#7B5BF2'}}>
      <div style={{fontSize:'32px',marginBottom:'12px'}}>⏳</div>
      <div style={{fontSize:'13px'}}>Cargando historial...</div>
    </div>
  );

  if (informes.length === 0) return (
    <div style={{background:'white',borderRadius:'14px',padding:'40px',border:'1px solid #E5E7EB',textAlign:'center',color:'#9CA3AF'}}>
      <div style={{fontSize:'40px',marginBottom:'12px'}}>📚</div>
      <div style={{fontSize:'13px'}}>No hay informes guardados aún. Generá el primer análisis ARIA desde el tab 🤖 ARIA.</div>
    </div>
  );

  return (
    <div style={{display:'grid',gridTemplateColumns:seleccionado?'280px 1fr':'1fr',gap:'14px'}}>
      <div>
        {informes.map(inf => (
          <div key={inf.id} onClick={() => setSeleccionado(inf)}
            style={{background:'white',borderRadius:'12px',padding:'14px 16px',marginBottom:'8px',border:`1.5px solid ${seleccionado?.id===inf.id?'#7B5BF2':'#E5E7EB'}`,cursor:'pointer',transition:'border-color 0.2s'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
              <div style={{fontSize:'13px',fontWeight:'700',color:'#1C3FAA'}}>📅 {inf.mes}</div>
              <div style={{fontSize:'10px',background:'#F0EEFF',color:'#7B5BF2',padding:'2px 8px',borderRadius:'6px',fontWeight:'600'}}>ARIA</div>
            </div>
            <div style={{fontSize:'11px',color:'#6B7280'}}>
              Total: <strong style={{color:'#1C3FAA'}}>${fmt(inf.datos_cnc?.total || 0)}</strong>
            </div>
            <div style={{fontSize:'10px',color:'#9CA3AF',marginTop:'4px'}}>
              {new Date(inf.fecha).toLocaleDateString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
            </div>
            <button onClick={e=>{e.stopPropagation();eliminar(inf.id);}} style={{marginTop:'8px',width:'100%',padding:'4px',borderRadius:'6px',border:'1px solid #fee2e2',background:'#fff5f5',color:'#dc2626',fontSize:'11px',cursor:'pointer',fontWeight:'600'}}>🗑 Eliminar</button>
          </div>
        ))}
      </div>

      {seleccionado && (
        <div style={{background:'white',borderRadius:'14px',padding:'20px',border:'1px solid #E5E7EB'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
            <div style={{fontSize:'14px',fontWeight:'700',color:'#1C3FAA'}}>📋 Informe — {seleccionado.mes}</div>
            <button onClick={() => setSeleccionado(null)} style={{background:'none',border:'1px solid #E5E7EB',borderRadius:'8px',padding:'4px 10px',cursor:'pointer',fontSize:'12px',color:'#6B7280'}}>✕ Cerrar</button>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'16px'}}>
            {[
              ['Fallas int.', seleccionado.datos_cnc?.fallaInternas, '#dc2626'],
              ['Fallas ext.', seleccionado.datos_cnc?.fallasExternas, '#d97706'],
              ['Prevención', seleccionado.datos_cnc?.prevencion, '#16a34a'],
              ['Evaluación', seleccionado.datos_cnc?.evaluacion, '#2A7FFF'],
            ].map(([label,val,color]) => (
              <div key={label} style={{background:'#F9FAFB',borderRadius:'10px',padding:'10px',border:'1px solid #E5E7EB',textAlign:'center'}}>
                <div style={{fontSize:'10px',color:'#6B7280',marginBottom:'4px'}}>{label}</div>
                <div style={{fontSize:'14px',fontWeight:'700',color}}>${fmt(val||0)}</div>
              </div>
            ))}
          </div>
          <div style={{background:'#F8F7FF',borderRadius:'12px',padding:'16px',border:'1px solid #E0D9FF',fontSize:'12px',lineHeight:'1.8',color:'#2F3E2E',whiteSpace:'pre-wrap',maxHeight:'500px',overflowY:'auto'}}>
            {seleccionado.informe_texto}
          </div>
        </div>
      )}
    </div>
  );
}
